import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  DocumentPageIcon,
  Inscription,
  Preloader,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import useProgress from '@/components/controls/DropScan/hooks/useProgress';
import { InnerProgressBar, ProgressBar } from '@/components/controls/DropScan/ProcessScan.styles';
import UploadFileByLinkContentWrapper from '@/modules/transaction/components/UploadFileByLink/UploadFileByLinkContentWrapper';

import { Step } from './uploadQrFile.types';

const PreloaderWrapper = styled(Box)`
  position: absolute;
  bottom: -6px;
  right: -6px;
`;

interface Props {
  setStep?: (step: Step) => void;
  onCancelUploading?: () => void;
}

export const UploadFileByQrLinkLoading: React.FC<Props> = ({ setStep, onCancelUploading }) => {
  const { t } = useTranslation();
  const { progress } = useProgress(50);

  const handleCancelUploading = useCallback(() => {
    onCancelUploading?.();
    setStep?.(Step.PROCESS);
  }, [setStep, onCancelUploading]);

  return (
    <UploadFileByLinkContentWrapper testId="loading-page">
      <Box justify="center" align="center">
        <Box style={{ position: 'relative' }}>
          <DocumentPageIcon size="72px" />
          <PreloaderWrapper background="background1" pad="spacing6" round="100%">
            <Preloader variant="gradient" color="white" size={20} />
          </PreloaderWrapper>
        </Box>

        <Box
          margin={{ top: 'spacing24', bottom: '50px' }}
          pad={{ horizontal: 'spacing32' }}
        >
          <Inscription
            size="large"
            weight={700}
            textAlign="center"
            margin={{ bottom: 'spacing12' }}
          >
            {t('We’re uploading your file.')}
            <br />
            {t('This should only take a few seconds.')}
          </Inscription>

          <Box margin={{ top: 'spacing12' }} align="center">
            <ProgressBar width="100% !important">
              <InnerProgressBar progress={progress} />
            </ProgressBar>
            <Inscription>{`${Math.floor(progress * 100)}% Uploading`}</Inscription>
          </Box>
        </Box>
        <Box width={{ width: '100%', max: '400px' }} pad={{ horizontal: 'spacing16' }}>
          <AppButton
            width="100%"
            testId="cancel-uploading"
            onClick={handleCancelUploading}
            buttonType="tertiary"
            color="red"
          >
            {t('Cancel Uploading')}
          </AppButton>
        </Box>
      </Box>
    </UploadFileByLinkContentWrapper>
  );
};
